import React, { useContext, useEffect, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { makeStyles } from "@mui/styles";
import { alpha } from "@mui/material/styles";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Divider from "@mui/material/Divider";
import { Badge, Tooltip } from "@mui/material";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import ContactPhoneOutlinedIcon from "@mui/icons-material/ContactPhoneOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import QuestionAnswerOutlinedIcon from "@mui/icons-material/QuestionAnswerOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import ViewKanbanOutlinedIcon from "@mui/icons-material/ViewKanban";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToy";
import BarChartIcon from "@mui/icons-material/BarChart";
import WebhookIcon from "@mui/icons-material/Webhook";
import EngineeringIcon from "@mui/icons-material/Engineering";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import ContentCutOutlinedIcon from "@mui/icons-material/ContentCutOutlined";

import { i18n } from "../translate/i18n";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { Can } from "../components/Can";

const useStyles = makeStyles((theme) => ({
  navItem: {
    borderRadius: 6,
    margin: "1px 6px",
    width: "calc(100% - 12px)",
    padding: "6px 10px",
    minHeight: 36,
    color: theme.palette.text.secondary,
    transition: "background 0.15s, color 0.15s",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.text.primary,
    },
    "& .MuiListItemIcon-root": {
      color: "inherit",
      minWidth: 32,
    },
    "& .MuiListItemText-primary": {
      fontSize: "0.8125rem",
      fontWeight: 500,
      color: "inherit",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  },
  navItemActive: {
    borderRadius: 6,
    margin: "1px 6px",
    width: "calc(100% - 12px)",
    padding: "6px 10px",
    minHeight: 36,
    backgroundColor: theme.palette.action.selected,
    borderLeft: `2px solid ${theme.palette.primary.main}`,
    paddingLeft: 8,
    color: theme.palette.primary.main,
    "& .MuiListItemIcon-root": {
      color: theme.palette.primary.main,
      minWidth: 32,
    },
    "& .MuiListItemText-primary": {
      fontSize: "0.8125rem",
      fontWeight: 600,
      color: theme.palette.primary.main,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.12),
    },
  },
  sectionLabel: {
    color: theme.palette.text.secondary,
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    padding: "12px 16px 4px",
    lineHeight: 1,
  },
}));

function NavItem({ icon, label, to, drawerOpen }) {
  const classes = useStyles();
  const location = useLocation();
  const isActive = to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  const LinkComponent = React.useMemo(
    () => React.forwardRef((props, ref) => <RouterLink to={to} ref={ref} {...props} />),
    [to]
  );

  const button = (
    <ListItemButton
      component={LinkComponent}
      className={isActive ? classes.navItemActive : classes.navItem}
      disableRipple={false}
    >
      <ListItemIcon>{icon}</ListItemIcon>
      {drawerOpen && <ListItemText primary={label} />}
    </ListItemButton>
  );

  if (!drawerOpen) {
    return (
      <li>
        <Tooltip title={label} placement="right" arrow>
          {button}
        </Tooltip>
      </li>
    );
  }

  return <li>{button}</li>;
}

const MainListItems = ({ drawerClose, drawerOpen }) => {
  const classes = useStyles();
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user } = useContext(AuthContext);
  const [connectionWarning, setConnectionWarning] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offline = whatsApps.filter(
          (w) => ["qrcode", "PAIRING", "DISCONNECTED", "TIMEOUT", "OPENING"].includes(w.status)
        );
        setConnectionWarning(offline.length > 0);
      }
    }, 2000);
    return () => clearTimeout(t);
  }, [whatsApps]);

  return (
    <div onClick={drawerClose}>
      <NavItem to="/" label="Dashboard" icon={<DashboardOutlinedIcon sx={{ fontSize: 18 }} />} drawerOpen={drawerOpen} />
      <NavItem
        to="/connections"
        label={i18n.t("mainDrawer.listItems.connections")}
        icon={
          <Badge badgeContent={connectionWarning ? "!" : 0} color="error" sx={{ "& .MuiBadge-badge": { fontSize: 9, minWidth: 14, height: 14 } }}>
            <SyncAltIcon sx={{ fontSize: 18 }} />
          </Badge>
        }
        drawerOpen={drawerOpen}
      />
      <NavItem to="/tickets" label={i18n.t("mainDrawer.listItems.tickets")} icon={<WhatsAppIcon sx={{ fontSize: 18 }} />} drawerOpen={drawerOpen} />
      <NavItem to="/contacts" label={i18n.t("mainDrawer.listItems.contacts")} icon={<ContactPhoneOutlinedIcon sx={{ fontSize: 18 }} />} drawerOpen={drawerOpen} />
      <NavItem to="/kanban" label="Kanban" icon={<ViewKanbanOutlinedIcon sx={{ fontSize: 18 }} />} drawerOpen={drawerOpen} />
      <Can
        role={user.profile}
        perform="financial-pages:view"
        yes={() => (
          <>
            <NavItem to="/service-orders" label="Ordens de Serviço" icon={<EngineeringIcon sx={{ fontSize: 18 }} />} drawerOpen={drawerOpen} />
            <NavItem to="/quotes" label="Orçamentos" icon={<RequestQuoteIcon sx={{ fontSize: 18 }} />} drawerOpen={drawerOpen} />
            <NavItem to="/receituarios" label="Receituários" icon={<DescriptionOutlinedIcon sx={{ fontSize: 18 }} />} drawerOpen={drawerOpen} />
            <NavItem to="/adesivos" label="Adesivos" icon={<ContentCutOutlinedIcon sx={{ fontSize: 18 }} />} drawerOpen={drawerOpen} />
          </>
        )}
      />
      <NavItem to="/quickAnswers" label={i18n.t("mainDrawer.listItems.quickAnswers")} icon={<QuestionAnswerOutlinedIcon sx={{ fontSize: 18 }} />} drawerOpen={drawerOpen} />

      <Can
        role={user.profile}
        perform="drawer-admin-items:view"
        yes={() => (
          <>
            <Divider sx={{ my: 1, mx: 1 }} />
            {drawerOpen && (
              <ListSubheader disableSticky className={classes.sectionLabel}>
                {i18n.t("mainDrawer.listItems.administration")}
              </ListSubheader>
            )}
            <NavItem to="/catalog" label="Catálogo" icon={<CategoryOutlinedIcon sx={{ fontSize: 18 }} />} drawerOpen={drawerOpen} />
            <NavItem to="/users" label={i18n.t("mainDrawer.listItems.users")} icon={<PeopleAltOutlinedIcon sx={{ fontSize: 18 }} />} drawerOpen={drawerOpen} />
            <NavItem to="/queues" label={i18n.t("mainDrawer.listItems.queues")} icon={<AccountTreeOutlinedIcon sx={{ fontSize: 18 }} />} drawerOpen={drawerOpen} />
            <NavItem to="/tags" label="Tags" icon={<LocalOfferOutlinedIcon sx={{ fontSize: 18 }} />} drawerOpen={drawerOpen} />
            <NavItem to="/keyword-rules" label="Auto-resposta" icon={<SmartToyOutlinedIcon sx={{ fontSize: 18 }} />} drawerOpen={drawerOpen} />
            <NavItem to="/webhooks" label="Webhooks" icon={<WebhookIcon sx={{ fontSize: 18 }} />} drawerOpen={drawerOpen} />
            <NavItem to="/reports" label="Relatórios" icon={<BarChartIcon sx={{ fontSize: 18 }} />} drawerOpen={drawerOpen} />
            <NavItem to="/user-activity" label="Atividade" icon={<AssignmentOutlinedIcon sx={{ fontSize: 18 }} />} drawerOpen={drawerOpen} />
            <NavItem to="/settings" label={i18n.t("mainDrawer.listItems.settings")} icon={<SettingsOutlinedIcon sx={{ fontSize: 18 }} />} drawerOpen={drawerOpen} />
          </>
        )}
      />
    </div>
  );
};

export default MainListItems;
